import { Howl, Howler } from 'howler';
import { soundPalette } from '../../config/soundPalette';

/**
 * SoundManager — singleton audio controller
 */
class SoundManager {
  private static instance: SoundManager;
  private sounds: Map<string, Howl> = new Map();
  private bgMusic: Howl | null = null;
  private readonly volumeStorageKey = 'slopeAudioVolume';
  private readonly mutedStorageKey = 'slopeAudioMuted';
  private readonly defaultMasterVolume = 0.35;
  // On phones and tablets the device's own volume buttons set the loudness and the
  // app's button is only sound on or off, so the game plays at full level there.
  private readonly deviceControlsVolume = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  // Loop and phase-change gains. Like soundPalette.ts, each lands on its agreed
  // level at master volume 1.0 (level = file loudness + 20*log10(gain), dBFS;
  // tools/sound-lab/audio/v2/shipped-mix.json).
  // Skiing: ski-loop.wav is -21 dBFS, so -27 dB at rest rising to -21 at top speed.
  private readonly skiLoopBaseVolume = 0.501;
  private readonly skiLoopMaxVolume = 1;
  // Tier wind (wind-loop.wav, -18.6 dBFS): -40 dB at tier 0 rising 3 dB a tier to
  // -28 at tier 4 (Whiteout); tier 5 (Aurora) is calmer at -32.5.
  private readonly tierWindVolumes = [0.085, 0.12, 0.169, 0.239, 0.337, 0.201];
  // Phase change (SharpskiSynth.mp3, -18.0 dBFS): -28 dB at tier 1 rising 1 dB a tier to -24 at tier 5.
  private readonly tierShiftVolumes = [0.315, 0.354, 0.397, 0.445, 0.499];
  // Turn swish (SkiSharp.mp3, -20.5 dBFS): random per turn between -30 and -24 dB.
  private readonly skiShiftVolumeRange = [0.336, 0.67] as const;
  private currentRunTier = -1;
  private confirmedCues = new Set<string>();
  private _masterVolume: number = this.deviceControlsVolume ? 1 : this.defaultMasterVolume;
  private _muted: boolean = false;
  private _pageVisible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
  private loopIds = new Map<string, number>();
  private resumePending = false;
  private resumeAttempted = false;
  private resumeAttemptId = 0;

  constructor() {
    this.loadPreferences();
    this.loadSounds();
    this.applyHowlerVolume();
  }

  public static getInstance(): SoundManager {
    if (!SoundManager.instance) {
      SoundManager.instance = new SoundManager();
    }
    return SoundManager.instance;
  }


  // Howler 2.2 maintains its own context state. Never suspend the context behind
  // its back or enqueue frame-by-frame play requests while it is resuming.
  public resumeAudio(userGesture = false) {
    if (this._muted || !this._pageVisible) return;
    const ctx = Howler.ctx;
    if (!ctx || ctx.state === 'closed') return;
    const engine = Howler as typeof Howler & { state?: string; _autoResume?: () => void };
    if (ctx.state === 'running' && engine.state === 'running') {
      if (this.resumePending) this.resumeAttemptId++;
      this.resumePending = false;
      this.resumeAttempted = false;
      return;
    }
    // Safari may leave resume() pending until an eligible interaction. Never
    // let that request block the next real gesture, but still coalesce frames.
    if (this.resumePending && !userGesture) return;
    if (this.resumeAttempted && !userGesture) return;
    this.resumeAttempted = true;
    this.resumePending = true;
    const attemptId = ++this.resumeAttemptId;
    void ctx.resume().then(() => {
      if (attemptId !== this.resumeAttemptId || ctx !== Howler.ctx || !this._pageVisible || this._muted) return;
      // Let Howler update its state and notify its own listeners as well.
      engine._autoResume?.();
    }).catch(() => { /* A later user gesture can retry browser audio unlock. */ })
      .finally(() => { if (attemptId === this.resumeAttemptId) this.resumePending = false; });
  }

  private _hardStopAll() {
    this.sounds.forEach(s => { if (s.state() === 'loaded') s.stop(); });
    this.loopIds.clear();
    this.bgMusic?.stop();
    this.currentRunTier = -1;
  }

  private canStartAudio() {
    const engine = Howler as typeof Howler & { state?: string };
    return !this._muted && this._pageVisible &&
      (!Howler.ctx || (Howler.ctx.state === 'running' && engine.state === 'running'));
  }

  private clampVolume(vol: number) {
    if (this.deviceControlsVolume) return 1;
    return Number.isFinite(vol) ? Math.max(0, Math.min(1, vol)) : this.defaultMasterVolume;
  }

  private loadPreferences() {
    try {
      const savedVolume = window.localStorage.getItem(this.volumeStorageKey);
      const savedMuted = window.localStorage.getItem(this.mutedStorageKey);

      if (savedVolume !== null) {
        this._masterVolume = this.clampVolume(Number(savedVolume));
      }

      if (savedMuted !== null) {
        this._muted = savedMuted === 'true';
      }
    } catch {
      this._masterVolume = this.clampVolume(this.defaultMasterVolume);
      this._muted = false;
    }
  }

  private savePreferences() {
    try {
      window.localStorage.setItem(this.volumeStorageKey, String(this._masterVolume));
      window.localStorage.setItem(this.mutedStorageKey, String(this._muted));
    } catch {}
  }

  private applyHowlerVolume() {
    Howler.volume(this._muted || !this._pageVisible ? 0 : this._masterVolume);
  }


  get masterVolume() { return this._masterVolume; }
  get muted() { return this._muted; }

  setMasterVolume(vol: number) {
    this._masterVolume = this.clampVolume(vol);
    this.applyHowlerVolume();
    this.savePreferences();
  }

  toggleMute(): boolean {
    this.setMuted(!this._muted);
    return this._muted;
  }

  setMuted(muted: boolean) {
    this._muted = muted;
    if (muted) this._hardStopAll();
    else this.resumeAudio();
    this.applyHowlerVolume();
    this.savePreferences();
  }


  handleHidden() {
    this._pageVisible = false;
    // Requests from the previous foreground session must not gate or complete
    // a new recovery attempt when the page returns.
    this.resumeAttemptId++;
    this.resumePending = false;
    this.resumeAttempted = false;
    this._hardStopAll();
    this.applyHowlerVolume();
    // Howler owns automatic suspension after sources have stopped.
  }

  handleVisible() {
    this._pageVisible = true;
    this.resumeAttempted = false;
    this._hardStopAll();
    this.applyHowlerVolume();
    this.resumeAudio();
  }

  public stopAll() { this._hardStopAll(); }

  playSFX(name: string) {
    if (!this.canStartAudio()) return;
    const sound = this.sounds.get(name);
    if (!sound) { console.warn(`SoundManager: "${name}" not found`); return; }
    // Never queue a confirmation to play later when an asset finally loads.
    if (sound.state() !== 'loaded' || Howler.ctx?.state === 'suspended') return;
    sound.stop();
    sound.play();
  }

  playConfirmedSFX(name: 'betPlace' | 'cashout', receipt: string) {
    const key = `${name}:${receipt}`;
    if (this.confirmedCues.has(key)) return;
    this.confirmedCues.add(key);
    if (this.confirmedCues.size > 64) this.confirmedCues.delete(this.confirmedCues.values().next().value!);
    this.playSFX(name);
  }

  private startLoop(name: string): number | undefined {
    if (!this.canStartAudio()) return;
    const loop = this.sounds.get(name);
    if (!loop || loop.state() !== 'loaded') return;
    const existing = this.loopIds.get(name);
    if (existing !== undefined) return existing;
    const id = loop.play();
    this.loopIds.set(name, id);
    return id;
  }

  startSkiLoop(speed: number) {
    this.resumeAudio();
    const id = this.startLoop('skiMain');
    if (id !== undefined) this.setSkiIntensity(speed);
  }

  stopSkiLoop() {
    this.stopLoop('skiMain');
  }

  private stopLoop(name: string) {
    const id = this.loopIds.get(name);
    if (id !== undefined) this.sounds.get(name)?.stop(id);
    this.loopIds.delete(name);
  }

  setSkiIntensity(speed: number) {
    if (!this.canStartAudio()) return;
    const loop = this.sounds.get('skiMain');
    const id = this.loopIds.get('skiMain');
    if (!loop || id === undefined) return;
    const safeSpeed = Number.isFinite(speed) ? Math.max(0, Math.min(45, speed)) : 0;
    const normalizedSpeed = Math.min(1, safeSpeed / 25);
    loop.volume(this.skiLoopBaseVolume + normalizedSpeed * (this.skiLoopMaxVolume - this.skiLoopBaseVolume), id);
    loop.rate(0.9 + (safeSpeed / 45) * 0.3, id);
  }

  setRunTier(tierIndex: number) {
    const id = this.startLoop('tierWind');
    if (id === undefined) return;
    const wind = this.sounds.get('tierWind')!;
    const safeTier = Number.isFinite(tierIndex) ? Math.max(0, Math.min(5, Math.floor(tierIndex))) : 0;
    const rates = [0.72, 0.8, 0.9, 1.02, 1.16, 0.86];
    if (safeTier !== this.currentRunTier) {
      wind.volume(this.tierWindVolumes[safeTier], id);
      wind.rate(rates[safeTier], id);
      this.currentRunTier = safeTier;
    }
  }

  playRunTierTransition(tierIndex: number) {
    if (!this.canStartAudio() || !(tierIndex >= 1)) return;
    const shift = this.sounds.get('tierShift');
    if (!shift || shift.state() !== 'loaded') return;
    const id = shift.play();
    shift.rate(Math.min(1.3, .88 + tierIndex * .07), id);
    shift.volume(this.tierShiftVolumes[Math.min(5, Math.floor(tierIndex)) - 1], id);
  }

  playAuroraMilestone(multiplier: number) {
    if (!this.canStartAudio() || multiplier < 40) return;
    this.playSFX('milestone');
  }

  resetRunTier() {
    this.stopLoop('tierWind');
    this.currentRunTier = -1;
  }

  playSkiShift() {
    if (!this.canStartAudio()) return;
    const shift = this.sounds.get('skiShift');
    if (!shift) return;
    const id = shift.play();
    const [min, max] = this.skiShiftVolumeRange;
    shift.rate(0.9 + Math.random() * 0.2, id);
    shift.volume(min + Math.random() * (max - min), id);
  }

  startMusic() { if (this.canStartAudio() && !this.bgMusic?.playing()) this.bgMusic?.play(); }
  stopMusic() { this.bgMusic?.stop(); }
  setMusicPlaybackRate(rate: number) { this.bgMusic?.rate(rate); }


  private loadSounds() {
    Object.entries(soundPalette).forEach(([name, cue]) => {
      this.sounds.set(name, new Howl({ src: [cue.src], volume: cue.volume }));
    });
    // Seamless loops: the levelled February skiing recording and the tier wind
    // with its loop-point click removed (see public/sounds/slope/README.md).
    this.sounds.set('skiMain',  new Howl({ src: ['/sounds/slope/ski-loop.wav'],  volume: 0, loop: true }));
    this.sounds.set('skiShift', new Howl({ src: ['/sounds/SkiSharp.mp3'], volume: this.skiShiftVolumeRange[0] }));
    this.sounds.set('tierWind', new Howl({ src: ['/sounds/slope/wind-loop.wav'], volume: 0, loop: true }));
    this.sounds.set('tierShift',new Howl({ src: ['/sounds/SharpskiSynth.mp3'], volume: this.tierShiftVolumes[0] }));
  }
}

export const soundManager = SoundManager.getInstance();
